import React, { useState, useMemo } from 'react';
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import {
  CustomerRecord,
  CustomersTableRow,
} from '../../models/customer.models';
import { GenericQueryResponse } from '../../models/dynamo.query.model';
import { Order, getComparator } from '../DocumentHubPage/tableUtills';
import { UpdateCustomerStatusModal } from './UpdateCustomerStatusModal';
import { useNavigate } from 'react-router';

interface CustomerCenterTable {
  customers: GenericQueryResponse<CustomerRecord>;
}

export interface HeadCell {
  disablePadding: boolean;
  id: keyof CustomersTableRow;
  label: string;
  numeric: boolean;
  icon: React.ReactNode;
}

interface EnhancedTableProps {
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof CustomersTableRow
  ) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
}

const headCells: readonly HeadCell[] = [
  {
    id: 'firstName',
    numeric: false,
    disablePadding: false,
    label: 'Name',
    icon: <PersonIcon />,
  },
  {
    id: 'email',
    numeric: false,
    disablePadding: false,
    label: 'Email',
    icon: <EmailIcon />,
  },
  {
    id: 'createdOn',
    numeric: false,
    disablePadding: false,
    label: 'Created On',
    icon: <CalendarMonthIcon />,
  },
  {
    id: 'contactPreferences',
    numeric: false,
    disablePadding: false,
    label: 'Contact Preferences',
    icon: <PhoneIcon />,
  },
];

function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler =
    (property: keyof CustomersTableRow) =>
    (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead className="table-head">
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={'left'}
            padding={'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={{ color: '#FFFFFF', fontWeight: 'bold', fontSize: 14 }}
          >
            <TableSortLabel
              key={`${headCell.id}-sort-label`}
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
              sx={{
                '& .MuiTableSortLabel-icon': {
                  color: '#FFFFFF !important',
                },
                '&.Mui-active': {
                  color: '#FFFFFF',
                },
                '&:hover': {
                  color: '#FFFFFF',
                },
                color: '#FFFFFF',
              }}
            >
              {headCell.icon}
              {headCell.label}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export function CustomerCenterTable(props: CustomerCenterTable) {
  const { customers } = props;

  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof CustomersTableRow>('firstName');
  const [openSelectActionDialog, setOpenSelectActionDialog] = useState(false);
  const [openChangeStatusUser, setOpenChangeStatusUser] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerRecord>();
  const [isSuccess, setIsSuccess] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState<
    'Style Board' | 'UpdateCustomerStatusModal' | 'Select'
  >('Select');
  const navigate = useNavigate();

  const handleRequestSort = (
    _event: React.MouseEvent<unknown>,
    property: keyof CustomersTableRow
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleClick = (
    _event: React.MouseEvent<unknown>,
    row: CustomersTableRow
  ) => {
    setSelectedCustomer(row as CustomerRecord);
    setOpenSelectActionDialog(true);
  };

  const handleSelectComponent = (
    component: 'Style Board' | 'UpdateCustomerStatusModal'
  ) => {
    setSelectedComponent(component);
    if (component === 'UpdateCustomerStatusModal') {
      setOpenChangeStatusUser(true);
    } else if (component === 'Style Board') {
      void navigate('/portal/style', { state: { customer: selectedCustomer } });
    }
  };

  const sortedCustomerItems = useMemo(() => {
    return [...customers.Items].sort(getComparator(order, orderBy));
  }, [customers.Items, order, orderBy]);

  return (
    <>
      <TableContainer component={Paper} className="table-container">
        <Table
          sx={{ minWidth: 750 }}
          aria-labelledby="tableTitle"
          size={'medium'}
        >
          <EnhancedTableHead
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={sortedCustomerItems.length}
          />
          <TableBody>
            {sortedCustomerItems.map((row, index) => {
              return (
                <TableRow
                  hover
                  onClick={(event) => handleClick(event, row)}
                  key={index}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell align="left">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {row.firstName} {row.lastName}
                    </Box>
                  </TableCell>
                  <TableCell align="left">{row.email}</TableCell>
                  <TableCell align="left">
                    {new Date(row.createdOn).toISOString().split('T')[0]}
                  </TableCell>
                  <TableCell align="left">{row.contactPreferences}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {selectedCustomer && (
        <Dialog
          open={openSelectActionDialog}
          onClose={() => setOpenSelectActionDialog(false)}
        >
          <DialogTitle>Select Action</DialogTitle>
          <DialogContent>
            <FormControl fullWidth>
              <Select
                labelId="component-select-label"
                value={selectedComponent}
                onChange={(event) =>
                  handleSelectComponent(
                    event.target.value as
                      | 'Style Board'
                      | 'UpdateCustomerStatusModal'
                  )
                }
                label="Select Component"
              >
                <MenuItem value="Select">Select</MenuItem>
                <MenuItem value="Style Board">Style Board</MenuItem>
                <MenuItem value="UpdateCustomerStatusModal">
                  Update Customer Status Modal
                </MenuItem>
              </Select>
            </FormControl>

            {selectedComponent === 'UpdateCustomerStatusModal' && (
              <UpdateCustomerStatusModal
                customer={selectedCustomer}
                setOpen={setOpenChangeStatusUser}
                open={openChangeStatusUser}
                setIsSuccess={setIsSuccess}
                setShowSnackbar={setShowSnackbar}
              />
            )}
          </DialogContent>
        </Dialog>
      )}
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowSnackbar(false)}
      >
        <Alert severity={isSuccess ? 'success' : 'error'}>
          {isSuccess
            ? 'Customer status updated successfully'
            : 'Something went wrong. Please try again.'}
        </Alert>
      </Snackbar>
    </>
  );
}
